import {
  faMagnifyingGlass,
  faPlus,
  faSpinnerThird,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@design-system/components/typography/typography';
import { Button } from '@design-system/components/button/button';
import { IconButton } from '@design-system/components/button/iconButton';
import { Input } from '@design-system/components/input/input';
import { textColorsVars } from '@design-system/vars.css';
import { SwapCatalogHit, SwapCatalogSearch } from '../model/useCatalogSearch';
import { SwapThumb } from './SwapThumb';
import * as styles from './CreatorApprovedSwaps.css';

type CatalogSearchProps = {
  search: SwapCatalogSearch;
  atCap: boolean;
  disabled?: boolean;
  onAdd: (hit: SwapCatalogHit) => void;
};

export const CatalogSearch = (p: CatalogSearchProps) => (
  <>
    <Input
      placeholder="Search"
      value={p.search.query}
      onChange={(event) => p.search.onQueryChange(event.target.value)}
      disabled={p.disabled}
      startIcon={
        p.search.searching
          ? {
              icon: faSpinnerThird,
              spin: true,
              color: textColorsVars['N-60'],
            }
          : {
              icon: faMagnifyingGlass,
              color: textColorsVars['N-60'],
            }
      }
    />
    <SearchResults {...p} />
  </>
);

const SearchResults = ({
  search,
  atCap,
  disabled,
  onAdd,
}: CatalogSearchProps) => {
  if (search.state.type === 'idle') return null;

  if (search.state.type === 'error') {
    return (
      <div className={styles.emptyState}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <Typography>
          Unable to load results right now. Please try again shortly
        </Typography>
        <Button color="primaryTonal" onClick={search.retry}>
          Retry
        </Button>
      </div>
    );
  }

  if (search.state.items.length === 0) {
    return (
      <div className={styles.emptyState}>
        <FontAwesomeIcon icon={faMagnifyingGlass} />
        <Typography>
          No matches for &quot;{search.state.query}&quot;.
        </Typography>
        <Typography>
          If this exercise already exists in your library, make sure it is added
          to a workout as a primary exercise first before adding it as a
          modification.
        </Typography>
      </div>
    );
  }

  return (
    <div className={styles.resultsList}>
      {search.state.items.map((hit) => (
        <div key={hit.exercise.name} className={styles.resultRow}>
          <SwapThumb media={hit.exercise.media} />
          <div className={styles.rowText}>
            <Typography>{hit.exercise.name}</Typography>
          </div>
          <IconButton
            variant="text"
            color="neutral"
            icon={faPlus}
            disabled={disabled || atCap}
            testId={`swap-add-${hit.exercise.name}`}
            onClick={() => onAdd(hit)}
          />
        </div>
      ))}
    </div>
  );
};
